import type { ICloudUser } from "../../types"
import { parseApiError } from "../../utils/regulatoryApi"
import { Alert, Button, Form, Input, Modal, Select, Space } from "antd"
import dayjs from "dayjs"
import { useRef, useState } from "react"
import { Link, useHistory } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"
import type { PhysicalIdentity } from "../../types/physical"
import {
  MethodTypeEN1177,
  NormType,
  Site,
  StorageEntityType,
  StorageRequest,
  UCPayloadType,
  UUID,
} from "../../generated/proto-ts/main"
import { useRegulatorySession } from "../../providers/regulatoryProvider"
import { useRegulatoryRequest } from "../../components/en1176/useRegulatoryAnswers"
import { TemplateSelector } from "../../components/en1176/CatalogAssignment"
import { physicalApi, physicalError } from "../../utils/physicalApi"
import { regulatoryApi } from "../../utils/regulatoryApi"
import { pbUUIDToUuid, uuidToPbUUID } from "../../utils/utils"
import { usePhysicalResource } from "../../components/physical/usePhysicalResource"
export function PhysicalInspectionCreate({
  site,
  onCancel,
}: {
  site: PhysicalIdentity
  onCancel: () => void
}) {
  const { user } = useRegulatorySession()
  const otherEnterprise = !!user && site.enterpriseUuid !== user.Enterprise.UUID
  const owners = usePhysicalResource(
    otherEnterprise ? `inspection-owners/${site.enterpriseUuid}` : "",
    async (signal) => {
      const response = await fetch(
        `/api/enterprise/users?enterprise_uuid=${encodeURIComponent(site.enterpriseUuid)}`,
        { signal, credentials: "same-origin", cache: "no-store" },
      )
      if (!response.ok) throw parseApiError(await response.text(), response.status)
      return (await response.json()) as ICloudUser[]
    },
  )
  const history = useHistory()
  const { request, connected } = useRegulatoryRequest()
  const [form] = Form.useForm()
  const [norm, setNorm] = useState(NormType.EN_1176)
  const [template, setTemplate] = useState<string | null>(null)
  const detail = usePhysicalResource(template ? `creation-template/${template}` : "", (signal) =>
    regulatoryApi.template(template!, signal),
  )
  const prepared = useRef<Site>()
  const [pendingId, setPendingId] = useState<string>()
  const created = useRef(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const create = async () => {
    const fields = await form.validateFields().catch(() => null)
    if (!fields || !user) return
    if (
      !prepared.current &&
      norm === NormType.EN_1176 &&
      (!template || !detail.data?.template.currentRevisionUuid)
    ) {
      setError("Choisissez un modèle de site dont la révision est disponible.")
      return
    }
    setBusy(true)
    setError(undefined)
    try {
      if (!prepared.current) {
        const id = uuidv4(),
          now = Date.now()
        prepared.current = new Site({
          uuid: uuidToPbUUID(id),
          user_uuid: uuidToPbUUID(otherEnterprise ? fields.owner : user.UUID),
          site_name: fields.name,
          mission_name: fields.mission,
          client_name: fields.client ?? "",
          address: fields.address ?? "",
          norm_type: norm,
          method_type_en_1177: norm === NormType.EN_1177 ? fields.method : undefined,
          execution_date: fields.date ? new Date(`${fields.date}T12:00:00`).getTime() : undefined,
          created_at: now,
          updated_at: now,
          ...(norm === NormType.EN_1176
            ? {
                regulatory_template_uuid: uuidToPbUUID(template!),
                regulatory_template_revision_uuid: uuidToPbUUID(
                  detail.data!.template.currentRevisionUuid!,
                ),
              }
            : {}),
        })
        setPendingId(id)
      }
      const entity = prepared.current!,
        id = pbUUIDToUuid(entity.uuid)
      if (!created.current) {
        const payload = await request(
          UCPayloadType.SICO_STORAGE_CREATE_ENTITY,
          new StorageRequest({
            entity_type: StorageEntityType.SITE,
            parent_uuid: entity.user_uuid,
            entity_uuid: entity.uuid,
            single_entity_data: entity.serializeBinary(),
          }),
        )
        if (pbUUIDToUuid(UUID.deserializeBinary(payload.data)) !== id)
          throw new Error("Le serveur a renvoyé une autre identité d’inspection.")
        created.current = true
      }
      const link = await physicalApi.link(id, false)
      if (link.physicalSiteUuid && link.physicalSiteUuid !== site.uuid)
        throw new Error(
          "Cette inspection a été rattachée à un autre site. Ouvrez-la pour vérifier son affectation.",
        )
      if (!link.physicalSiteUuid)
        await physicalApi.saveLink(id, false, {
          expectedVersion: link.version,
          physicalSiteUuid: site.uuid,
          reason: "Nouvelle inspection créée depuis le dossier du site",
        })
      history.push(norm === NormType.EN_1176 ? `/regulatory/sites/${id}` : `/sites/${id}`)
    } catch (e) {
      setError(physicalError(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open
      title="Nouvelle inspection sur ce site"
      onCancel={onCancel}
      closable={!busy}
      maskClosable={false}
      cancelButtonProps={{ disabled: busy }}
      cancelText="Fermer"
      onOk={create}
      okText={pendingId ? "Réessayer avec la même inspection" : "Créer l’inspection"}
      confirmLoading={busy}
      okButtonProps={{ disabled: !connected || site.archived }}
    >
      <Alert
        type="info"
        message="Les coordonnées sont proposées depuis le dossier du site. Vérifiez-les pour cette inspection. Les réponses précédentes ne sont pas reprises."
        style={{ marginBottom: 12 }}
      />
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {pendingId && (
        <Alert
          type="warning"
          message="Une identité d’inspection a été réservée. Réessayer utilise la même inspection ; fermer ne la supprime pas."
          description={
            <Link to={`/sites/${pendingId}`}>
              Ouvrir l’inspection enregistrée si sa création a été confirmée
            </Link>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {!connected && (
        <Alert type="warning" message="Connectez-vous au cloud pour créer une inspection." />
      )}
      <Form
        form={form}
        layout="vertical"
        disabled={busy || !!pendingId}
        initialValues={{
          name: site.name,
          address: site.address ?? "",
          mission: "",
          client: "",
          date: dayjs().format("YYYY-MM-DD"),
          method: MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE,
        }}
      >
        {otherEnterprise && (
          <Form.Item
            name="owner"
            label="Responsable de l’inspection"
            rules={[
              { required: true, message: "Choisissez un utilisateur de l’entreprise du site" },
            ]}
          >
            <Select
              loading={owners.loading}
              options={(owners.data ?? []).map((owner) => ({
                value: owner.UUID,
                label: `${owner.FirstName} ${owner.LastName}`.trim() || owner.Username,
              }))}
            />
          </Form.Item>
        )}
        {owners.error && <Alert type="error" message={physicalError(owners.error)} />}
        <Form.Item label="Type d’inspection">
          <Select
            value={norm}
            onChange={setNorm}
            options={[
              { value: NormType.EN_1176, label: "Réglementaire EN 1176" },
              { value: NormType.EN_1177, label: "HIC EN 1177" },
              { value: NormType.EN_12503, label: "EN 12503" },
            ]}
          />
        </Form.Item>
        {norm === NormType.EN_1176 && (
          <Form.Item label="Modèle et révision de l’inspection">
            <TemplateSelector
              value={template}
              onChange={setTemplate}
              scope="site"
              disabled={busy || !!pendingId}
            />
            {detail.data && (
              <p>
                Révision exacte v{detail.data.template.sourceVersion} ·{" "}
                {detail.data.template.validated ? "Relue" : "Non relue"}
              </p>
            )}
            {detail.error && <Alert type="error" message={physicalError(detail.error)} />}
          </Form.Item>
        )}
        {norm === NormType.EN_1177 && (
          <Form.Item name="method" label="Méthode">
            <Select
              options={[
                {
                  value: MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE,
                  label: "Conformité de l’atténuation d’impact",
                },
                {
                  value: MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION,
                  label: "Détermination de la hauteur de chute critique",
                },
              ]}
            />
          </Form.Item>
        )}
        <Form.Item
          name="name"
          label="Nom du site dans cette inspection"
          rules={[{ required: true, whitespace: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="mission"
          label="Intitulé de la mission"
          rules={[{ required: true, whitespace: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="client" label="Client">
          <Input />
        </Form.Item>
        <Form.Item name="address" label="Adresse de l’inspection">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="date" label="Date d’inspection" rules={[{ required: true }]}>
          <Input type="date" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
